import { Alert, Anchor, Button, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { IconAlertCircle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { DEFAULT_REGISTRATION_FORM, registerSchema, type RegisterInput } from '@creativitas/shared';
import { toUserMessage } from '@/lib/api';
import { storedUtm } from '@/lib/utm';
import { fetchRegistrationForm } from './api';
import { AuthLayout } from './AuthLayout';
import { ConsentCheckbox } from './ConsentCheckbox';
import { useRegister } from './useSession';

export function RegisterPage() {
  const registerMutation = useRegister();

  /*
    Надписи и согласия приходят настройкой: это юридические документы, их
    правит владелец платформы в администрировании. Пока настройка не пришла,
    показываются те же значения по умолчанию, что вернёт сервер, — форма не
    должна моргать пустотой на месте согласий.
  */
  const settings = useQuery({
    queryKey: ['auth', 'registration-form'],
    queryFn: fetchRegistrationForm,
    staleTime: 5 * 60 * 1000,
  });
  const registration = settings.data ?? DEFAULT_REGISTRATION_FORM;

  const [accepted, setAccepted] = useState<string[]>([]);
  const [consentError, setConsentError] = useState(false);
  /*
    Обязательны только согласия, отмеченные такими в настройке: на обработку
    данных — да, на рассылки — нет. Отказ от рекламы не мешает регистрации
    (концепция v0.3, п. 5).
  */
  const required = registration.consents.filter((consent) => consent.required);
  const requiredAccepted = required.every((consent) => accepted.includes(consent.id));

  const toggle = (id: string, on: boolean) => {
    setAccepted((current) => (on ? [...current, id] : current.filter((item) => item !== id)));
    setConsentError(false);
  };

  const form = useForm<RegisterInput>({
    initialValues: { displayName: '', email: '', password: '', acceptedConsents: [] },
    validate: zodResolver(registerSchema),
    validateInputOnBlur: true,
  });

  return (
    <AuthLayout title={registration.heading} subtitle={registration.subheading}>
      <form
        onSubmit={form.onSubmit((values) => {
          /*
            Форма не отправляется без обязательных галочек. Сервер проверяет
            это ещё раз: согласие — юридический факт, и запрос в обход формы
            должен отклоняться так же.
          */
          if (!requiredAccepted) {
            setConsentError(true);
            return;
          }
          registerMutation.mutate({ ...values, acceptedConsents: accepted, source: storedUtm() });
        })}
        noValidate
      >
        <Stack gap="md">
          {registerMutation.isError ? (
            <Alert color="red" icon={<IconAlertCircle size={17} />} title="Не удалось создать аккаунт">
              {toUserMessage(registerMutation.error)}
            </Alert>
          ) : null}

          <TextInput
            label="Как вас зовут"
            placeholder="Имя, которое увидят участники"
            autoComplete="name"
            required
            {...form.getInputProps('displayName')}
          />

          <TextInput
            label="Email"
            placeholder="you@example.com"
            type="email"
            autoComplete="email"
            required
            {...form.getInputProps('email')}
          />

          <PasswordInput
            label="Пароль"
            placeholder="Не короче 8 символов"
            autoComplete="new-password"
            required
            {...form.getInputProps('password')}
          />

          <Stack gap="xs">
            {registration.consents.map((consent) => (
              <ConsentCheckbox
                key={consent.id}
                consent={consent}
                checked={accepted.includes(consent.id)}
                onChange={(on) => toggle(consent.id, on)}
                error={consentError && consent.required && !accepted.includes(consent.id)}
              />
            ))}
            {consentError ? (
              <Text size="xs" c="var(--danger)">
                {required.length === 1
                  ? 'Чтобы зарегистрироваться, отметьте обязательное согласие.'
                  : 'Чтобы зарегистрироваться, отметьте обязательные согласия.'}
              </Text>
            ) : null}
          </Stack>

          <Button type="submit" fullWidth loading={registerMutation.isPending}>
            Создать аккаунт
          </Button>
        </Stack>
      </form>

      <Text size="sm" c="var(--text-muted)" ta="center" mt="md">
        Уже зарегистрированы?{' '}
        <Anchor component={Link} to="/login">
          Войти
        </Anchor>
      </Text>
    </AuthLayout>
  );
}
